// ── Day View Derivation ──
// Day columns are not entities: they derive from the trip's startDate–endDate.
// A block belongs to every date whose display-zone instant interval it
// intersects. The column zone is the trip default with sparse per-date
// overrides. Blocks are sorted by start instant.

import type { Trip, Schedule } from "@poi-planner/shared";
import type {
  AllDayTripNote,
  DayBlock,
  DayView,
  ScheduledTripNote,
} from "@poi-planner/shared";
import { enumerateDates, epochMs } from "./zonedTime";
import { dayStart } from "./timeGrid";

function isScheduledNote(
  note: Schedule["notes"][number],
): note is ScheduledTripNote {
  return note.startTime != null && note.endTime != null;
}

function isAllDayNote(note: Schedule["notes"][number]): note is AllDayTripNote {
  return (
    note.startTime == null &&
    note.endTime == null &&
    note.allDayStartDate != null &&
    note.allDayEndDate != null
  );
}

export function computeDayViews(trip: Trip, schedule: Schedule): DayView[] {
  const dates = enumerateDates(trip.startDate, trip.endDate);

  const zoneForDate = (date: string): string =>
    schedule.timezoneOverrides[date] ?? trip.defaultTimezone;

  // Each date's window runs from its own zoned midnight up to the next
  // date's zoned midnight, so per-date timezone overrides can't leave an
  // instant gap between two dates (a block landing in that gap would
  // otherwise intersect no date and vanish from every column).
  const windowStarts = dates.map(
    (date) => dayStart(date, zoneForDate(date)).epochMilliseconds,
  );
  const windowEnds = dates.map((date, index) =>
    index + 1 < dates.length
      ? windowStarts[index + 1]
      : dayStart(date, zoneForDate(date)).add({ days: 1 }).epochMilliseconds,
  );

  const blocksByDate = new Map<string, DayBlock[]>();
  const allDayBlocksByDate = new Map<string, DayView["allDayBlocks"]>();
  for (const date of dates) {
    blocksByDate.set(date, []);
    allDayBlocksByDate.set(date, []);
  }

  const intersectedDates = (startTime: string, endTime: string): string[] => {
    const start = epochMs(startTime);
    const end = epochMs(endTime);
    if (end <= start) return [];
    return dates.filter(
      (_date, index) => start < windowEnds[index] && end > windowStarts[index],
    );
  };

  for (const sp of schedule.scheduledPOIs) {
    const coveredDates = intersectedDates(sp.startTime, sp.endTime);
    coveredDates.forEach((date, index) => {
      blocksByDate.get(date)?.push({
        type: "poi",
        id: sp.id,
        scheduledPOI: sp,
        segment: {
          index,
          count: coveredDates.length,
          isStart: index === 0,
          isEnd: index === coveredDates.length - 1,
        },
      });
    });
  }

  for (const note of schedule.notes) {
    if (isAllDayNote(note)) {
      const coveredDates = dates.filter(
        (date) => date >= note.allDayStartDate && date <= note.allDayEndDate,
      );
      coveredDates.forEach((date, index) => {
        allDayBlocksByDate.get(date)?.push({
          id: note.id,
          note,
          segment: {
            index,
            count: coveredDates.length,
            isStart: index === 0,
            isEnd: index === coveredDates.length - 1,
          },
        });
      });
      continue;
    }
    if (!isScheduledNote(note)) continue; // inbox notes have no day
    const coveredDates = intersectedDates(note.startTime, note.endTime);
    coveredDates.forEach((date, index) => {
      blocksByDate.get(date)?.push({
        type: "note",
        id: note.id,
        note,
        segment: {
          index,
          count: coveredDates.length,
          isStart: index === 0,
          isEnd: index === coveredDates.length - 1,
        },
      });
    });
  }

  return dates.map((date) => {
    const blocks = blocksByDate.get(date) ?? [];
    blocks.sort((a, b) => {
      const aStart =
        a.type === "poi" ? a.scheduledPOI.startTime : a.note.startTime;
      const bStart =
        b.type === "poi" ? b.scheduledPOI.startTime : b.note.startTime;
      return epochMs(aStart) - epochMs(bStart);
    });
    return {
      date,
      zone: zoneForDate(date),
      blocks,
      allDayBlocks: (allDayBlocksByDate.get(date) ?? []).sort((a, b) =>
        a.note.name.localeCompare(b.note.name),
      ),
    };
  });
}

/** Start time of a block (zoned string). */
export function blockStartTime(block: DayBlock): string {
  return block.type === "poi"
    ? block.scheduledPOI.startTime
    : block.note.startTime;
}

/** End time of a block (zoned string). */
export function blockEndTime(block: DayBlock): string {
  return block.type === "poi" ? block.scheduledPOI.endTime : block.note.endTime;
}
